import i18n from '@breward/i18n';
import { Button } from '../Button/Button';
import { TextInput } from '../TextInput/TextInput';
import { useTranslation } from 'react-i18next';

// Map simple language codes to full language-region codes
const languageMap: Record<string, string> = {
    'en': 'en-GB',
    'pl': 'pl-PL'
};

export interface AppHeaderProps {
    logoSrc?: string;
    showCommandPalette?: boolean;
    onSearch?: (query: string) => void;
    onMenuClick?: () => void;
}

const AppHeader = ({ logoSrc, showCommandPalette = false, onSearch, onMenuClick }: AppHeaderProps) => {
    const [t] = useTranslation();

    // Function to change language with proper region code
    const changeLanguageWithRegion = (langCode: string) => {
        const fullLanguageCode = languageMap[langCode] || langCode;

        // Change the language in i18n
        i18n.changeLanguage(fullLanguageCode);

        // Update localStorage directly to ensure format is correct
        localStorage.setItem('i18nextLng', fullLanguageCode);
    };

    const handleLanguageChange = (language: string) => {
        changeLanguageWithRegion(language);
    };

    const openCommandPalette = () => {
        const event = new KeyboardEvent('keydown', {
            bubbles: true,
            cancelable: true,
            metaKey: true,
            key: 'k'
        });
        document.dispatchEvent(event);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && e.currentTarget.value.trim() !== '') {
            onSearch?.(e.currentTarget.value.trim());
            e.currentTarget.value = '';
        }
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-30 bg-white/95 dark:bg-content-950/90 backdrop-blur-sm border-b border-primary-100 dark:border-primary-800/60 shadow-sm dark:shadow-content-950/30">
            <div className="mx-auto flex h-14 w-full min-w-0 items-center justify-between px-4">
                {/* Logo */}
                <div className="flex min-w-0 items-center gap-2">
                    {logoSrc && <img src={logoSrc} alt="Breward" className="h-7 max-w-[12rem] object-contain" />}
                </div>

                {/* Right side: search, language + command palette */}
                <div className="flex flex-shrink-0 items-center gap-3">
                    {/* Search Input (Desktop) */}
                    {onSearch && (
                        <div className="hidden sm:block group">
                            <TextInput
                                type="text"
                                autoComplete="off"
                                placeholder={t('global:shared.searchPlaceholder', 'Entry code...')}
                                onKeyDown={handleSearchKeyDown}
                                startAddon={<div className="icon-[ph--magnifying-glass] h-4 w-4" />}
                                containerClassName="w-48 transition-all duration-300 group-focus-within:w-64"
                                inputClassName="!min-h-8 h-8 py-0 text-sm"
                            />
                        </div>
                    )}

                    {/* Language selector (Hidden on mobile) */}
                    <div className="relative hidden sm:block mr-8">
                        <select
                            className="appearance-none bg-transparent text-primary-600 dark:text-primary-300 border border-primary-200 dark:border-primary-700 rounded-lg px-2 py-1.5 pr-7 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                            onChange={(e) => handleLanguageChange(e.target.value)}
                            value={i18n.language.substring(0, 2)}
                            aria-label={t('global:shared.language', 'Language')}
                        >
                            <option value="en">EN</option>
                            <option value="pl">PL</option>
                        </select>
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <div className="icon-[ph--caret-down] text-primary-500 dark:text-primary-400 h-3.5 w-3.5" />
                        </div>
                    </div>

                    {/* Command palette button */}
                    {showCommandPalette && (
                        <Button
                            htmlType="button"
                            type="base"
                            variant="ghost"
                            onClick={() => openCommandPalette()}
                            className="hidden sm:flex items-center gap-2 rounded-lg bg-primary-50 px-2.5 py-1.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-800/30"
                            aria-label={t('global:shared.commandPalette', 'Command Palette')}
                        >
                            <kbd className="flex items-center border border-primary-200 dark:border-primary-700 rounded px-1 text-xs text-primary-500 dark:text-primary-400 font-mono">
                                ⌘K
                            </kbd>
                        </Button>
                    )}

                    {/* Mobile Menu Toggle */}
                    {onMenuClick && (
                        <button
                            type="button"
                            onClick={onMenuClick}
                            className="lg:hidden p-2 bg-transparent border-0 text-primary-600 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg focus:outline-none flex items-center justify-center cursor-pointer"
                            aria-label={t('global:shared.menu', 'Menu')}
                        >
                            <div className="icon-[ph--list] h-6 w-6" />
                        </button>
                    )}
                </div>
            </div>
        </header>
    );
};

export default AppHeader; 
